import React, { useEffect, useState } from "react";
import { Form, Input, Select, Row, Col, Button, Card, Radio } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  adminAddProduct,
  adminEditProduct,
  getAdminProduct,
  resetProduct,
} from "../../../appRedux/actions";
import { getRole } from "../../../helpers";
import "./add-product.css";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

const isDiagnosticAdmin = () => getRole() === "DIAGNOSTIC_ADMIN";

const AddProduct = (props) => {
  const { product_id } = props.match.params;
  const { isProductAdded, isLoaded, productItem, isProductEdited } =
    useSelector(({ adminProducts }) => adminProducts);
  const [editFlag, setEditFlag] = useState(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  useEffect(() => {
    if (product_id) {
      setEditFlag(true);
      dispatch(getAdminProduct(product_id));
    }
  }, []);

  useEffect(() => {
    if (isProductAdded || isProductEdited) {
      form.resetFields();
      navigate("/admin/product-list");
    }
    if (isLoaded && editFlag) {
      form.setFieldsValue(productItem);
    }
  }, [isProductAdded, isProductEdited, isLoaded]);

  const navigateBack = () => {
    navigate(-1);
  };

  const navigateToProductList = () => {
    navigate("/admin/product-list");
  };

  const onFinish = (values) => {
    if (editFlag) {
      dispatch(
        adminEditProduct(product_id, values, () => {
          dispatch(resetProduct());
        }),
      );
    } else {
      dispatch(
        adminAddProduct(values, () => {
          dispatch(resetProduct());
        }),
      );
    }
  };

  return (
    <div>
      <Row>
        <Col span={24}>
          <h1 className="title gx-mb-4">
            <LeftOutlined className="mr-5" onClick={navigateBack} />
            {editFlag ? "Edit Product" : "Add Product"}
          </h1>
        </Col>
        <Col span={24}>
          <Card className="gx-card">
            <Row>
              <Col span={22}>
                <Form
                  {...formItemLayout}
                  form={form}
                  name="add-product"
                  onFinish={onFinish}
                  scrollToFirstError
                >
                  <Form.Item
                    name="product_name"
                    label="Product Name"
                    rules={[
                      { required: true, message: "Please enter product name!" },
                    ]}
                  >
                    <Input id="add-product-name" />
                  </Form.Item>
                  <Form.Item
                    name="category"
                    label="Category"
                    rules={[
                      { required: true, message: "Please select category!" },
                    ]}
                    initialValue={isDiagnosticAdmin() ? "Diagnostics" : undefined}
                  >
                    <Radio.Group disabled={editFlag || isDiagnosticAdmin()}>
                      {!isDiagnosticAdmin() && (
                        <>
                          <Radio id="add-product-fdf" value="FDF">
                            FDF
                          </Radio>
                          <Radio id="add-product-api" value="API">
                            API
                          </Radio>
                        </>
                      )}
                      <Radio id="add-product-diagnostics" value="Diagnostics">
                        Diagnostics
                      </Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item label="Stages">
                    <Row>
                      <Col span={8}>
                        <Row>
                          <span className="stages-title">FDF</span>
                        </Row>
                        <Row>
                          <ol>
                            <li> Development set-up</li>
                            <li> Trial Batch</li>
                            <li> Pilot BE</li>
                            <li> Exhibit batches</li>
                            <li> Pivotal BE</li>
                            <li> Stability loading in different packs</li>
                            <li> Stability data 6 months</li>
                            <li> Dossier Compilation</li>
                            <li> DF Dossier Filing USFDA</li>
                            <li> DF Dossier Filing WHO</li>
                            <li>USFDA Approval (FDF)</li>
                            <li>WHO-PQ Approval (FDF)</li>
                          </ol>
                        </Row>
                      </Col>
                      <Col span={8}>
                        <Row>
                          <span className="stages-title">API</span>
                        </Row>
                        <Row>
                          <ol>
                            <li>Investigation and route selection</li>
                            <li>R&D Batch API</li>
                            <li>Lab scale batch API</li>
                            <li>RM Procurement</li>
                            <li>API Trial Batch</li>
                            <li>API Validation Batches</li>
                            <li>API Stability loading</li>
                            <li>API Stability Study</li>
                            <li>DMF Compilation</li>
                            <li>DMF Filing USFDA</li>
                            <li>DMF Filing WHO-PQ</li>
                            <li>USFDA Approval (API)</li>
                            <li>WHO-PQ Approval (API)</li>
                          </ol>
                        </Row>
                      </Col>
                      <Col span={8}>
                        <Row>
                          <span className="stages-title">Diagnostics</span>
                        </Row>
                        <Row>
                          <ol>
                            <li>Facility and equipment IQ, OQ, and PQ</li>
                            <li>GMP certification</li>
                            <li>ISO13485 certification</li>
                            <li>Validation batches for regulatory dossier submission</li>
                            <li>Analytical studies for regulatory dossier submission</li>
                            <li>Accelerated stability studies for regulatory dossier submission</li>
                            <li>Real time stability studies for regulatory dossier submission</li>
                            <li>Clinical studies for regulatory dossier submission</li>
                            <li>Dossier filing - Local</li>
                            <li>Dossier filing - ERPD</li>
                            <li>Dossier filing - WHO PQ</li>
                            <li>Local approval</li>
                            <li>ERPD approval</li>
                            <li>WHO PQ approval</li>
                            <li>First production batch</li>
                          </ol>
                        </Row>
                      </Col>
                    </Row>
                  </Form.Item>

                  <Form.Item
                    name="therapy_area"
                    label="Therapy Area"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Please select therapy area!",
                      },
                    ]}
                  >
                    <Select
                      id="add-product-status"
                      placeholder="Please select therapy area"
                    >
                      <Option id="add-product-hiv" value="HIV">
                        HIV
                      </Option>
                      <Option id="add-product-hcv" value="HCV">
                        HCV
                      </Option>
                      <Option id="add-product-hbv" value="HBV">
                        HBV
                      </Option>
                      <Option id="add-product-covid" value="Covid-19">
                        Covid-19
                      </Option>
                      <Option id="add-product-cancer" value="Cancer">
                        Cancer
                      </Option>
                      <Option id="add-product-malaria" value="Malaria">
                        Malaria
                      </Option>
                    </Select>
                  </Form.Item>

                  <Form.Item className="flex-d-row-reverse">
                    <Button
                      id="add-product-submit"
                      type="primary"
                      htmlType="submit"
                    >
                      {editFlag ? "Edit Product" : "Add Product"}
                    </Button>
                    <Button
                      id="add-product-cancel"
                      onClick={navigateToProductList}
                    >
                      Cancel
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
              <Col span={2}></Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AddProduct;
